import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Shared behaviour for every list screen (present and future), per the project
 * rule: lists render in pages of 50 rows and grow by 50 as the user nears the
 * end, and the list state (filters + how far the user scrolled/loaded) survives
 * a back-navigation or a page refresh.
 *
 * The content scrolls inside the unified shell's `.ush-main`, not the window,
 * so scroll is saved/restored on that element.
 */

export const LIST_PAGE_SIZE = 50;

/** The shell's scroll container (the main content region). */
function scrollContainer(): HTMLElement | null {
  return document.querySelector('.ush-main');
}

/**
 * State backed by sessionStorage, so it comes back after a refresh or a
 * back-navigation (and is scoped to the browser tab). Falls back to in-memory
 * state when storage is unavailable (private mode, blocked).
 */
export function usePersistedState<T>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw !== null ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (v: T) => {
      setValue(v);
      try {
        sessionStorage.setItem(key, JSON.stringify(v));
      } catch {
        /* storage unavailable — keep in-memory only */
      }
    },
    [key],
  );
  return [value, set];
}

/**
 * Incremental rendering: expose how many of `total` rows to show, starting at
 * one page and growing by a page whenever the sentinel (placed right after the
 * rows) comes near the viewport. The count is persisted so the user returns to
 * the same depth after a refresh/back.
 */
export function useIncremental(
  total: number,
  storageKey: string,
  step: number = LIST_PAGE_SIZE,
): { visible: number; sentinelRef: (el: HTMLElement | null) => void; reset: () => void } {
  const [count, setCountState] = useState<number>(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw !== null ? Number(raw) || step : step;
    } catch {
      return step;
    }
  });
  const countRef = useRef(count);
  countRef.current = count;
  const totalRef = useRef(total);
  totalRef.current = total;
  const stepRef = useRef(step);
  stepRef.current = step;

  const setCount = useCallback(
    (n: number) => {
      setCountState(n);
      try {
        sessionStorage.setItem(storageKey, String(n));
      } catch {
        /* ignore */
      }
    },
    [storageKey],
  );

  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback(
    (el: HTMLElement | null) => {
      observerRef.current?.disconnect();
      if (!el) return;
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            const next = Math.min(countRef.current + stepRef.current, totalRef.current);
            if (next !== countRef.current) setCount(next);
          }
        },
        { rootMargin: '600px 0px' },
      );
      io.observe(el);
      observerRef.current = io;
    },
    [setCount],
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const reset = useCallback(() => setCount(stepRef.current), [setCount]);

  const visible = Math.min(Math.max(count, step), Math.max(total, 0));
  return { visible, sentinelRef, reset };
}

/**
 * Save and restore the shell's scroll position for a screen, keyed by `key`.
 * Restores once `ready` is true (i.e. the content has rendered enough rows to
 * reach the saved offset).
 */
export function useScrollRestore(key: string, ready: boolean): void {
  const restored = useRef(false);

  useEffect(() => {
    const el = scrollContainer();
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        try {
          sessionStorage.setItem(key, String(el.scrollTop));
        } catch {
          /* ignore */
        }
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [key]);

  useEffect(() => {
    if (!ready || restored.current) return;
    const el = scrollContainer();
    if (!el) return;
    restored.current = true;
    let saved = 0;
    try {
      saved = Number(sessionStorage.getItem(key)) || 0;
    } catch {
      saved = 0;
    }
    if (saved > 0) {
      // Wait for layout so the rendered rows are tall enough to reach `saved`.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          el.scrollTop = saved;
        }),
      );
    }
  }, [key, ready]);
}

/** Scroll the shell content back to the top (e.g. after changing a filter). */
export function scrollListToTop(): void {
  const el = scrollContainer();
  if (el) el.scrollTop = 0;
}
